/** Locale context using the shared, language-neutral message catalog. */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { t as translate, type Locale, type TranslateParams } from '@toxofollow/shared';

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: TranslateParams) => string;
}

const I18nContext = createContext<I18nValue | null>(null);
const STORAGE_KEY = 'toxofollow.locale';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    (localStorage.getItem(STORAGE_KEY) as Locale) || 'fr',
  );
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
    document.documentElement.lang = l;
  }, []);
  const t = useCallback((key: string, params?: TranslateParams) => translate(locale, key, params), [locale]);
  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
